import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { X, ExternalLink, Folder, ChevronRight, TrendingUp, MoreVertical, Trash2, Plus, Camera } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import DeleteConfirmationModal from './DeleteConfirmationModal';
import { supabase } from '../lib/supabase';
import { useBusiness } from '../contexts/BusinessContext';
import { triggerGcalSync } from '../lib/bookings';
import toast from 'react-hot-toast';
import ScheduleFieldQuoteModal from './ScheduleFieldQuoteModal';
import CreateBookingModal from './CreateBookingModal';
import BookingStatusBar from './booking/BookingStatusBar';
import ScheduleTimeline from './booking/ScheduleTimeline';
import BookingCustomerInfo from './booking/BookingCustomerInfo';
import BookingResourceList from './booking/BookingResourceList';
import SelectResourceModal from './manage/SelectResourceModal';
import CustomerDetailsModal from './CustomerDetailsModal';
import MeasurementItem from './MeasurementItem';
import AddMeasurementModal from './AddMeasurementModal';
import PhotoCaptureModal from './PhotoCaptureModal';
import {
  listFieldQuotePhotos,
  uploadFieldQuotePhoto,
  deleteFieldQuotePhoto,
  fieldQuotePhotoUrl,
  type FieldQuotePhoto,
} from '../services/fieldQuotePhotoService';
import { usePermissionCheck } from '../hooks/usePermissionCheck';
import { getCustomer, type Customer } from '../lib/customers';
import {
  getFieldQuoteMeasurements,
  addProjectMeasurement,
  updateProjectMeasurement,
  deleteProjectMeasurement,
  type ProjectMeasurement,
  type AddProjectMeasurementParams,
  type UpdateProjectMeasurementParams
} from '../lib/projectMeasurements';

/** Map DB field quote status to BookingStatusBar / header format (underscores). */
function normalizeFqStatus(raw: string | null | undefined): 'scheduled' | 'in_progress' | 'completed' {
  const s = (raw || 'scheduled').toLowerCase().replace(/\s+/g, '_');
  if (s === 'in_progress') return 'in_progress';
  if (s === 'completed') return 'completed';
  return 'scheduled';
}

function barStatusToDb(bar: string): string {
  return bar === 'in_progress' ? 'in progress' : bar;
}

type FieldQuoteResourceLink = {
  id: string;
  resource_id: string;
  role: string | null;
  resource: {
    id: string;
    first_name: string;
    last_name: string;
    avatar_url?: string | null;
  } | null;
};

type FieldQuoteAssetLink = {
  id: string;
  asset_id: string;
  start_date: string | null;
  end_date: string | null;
  asset: {
    id: string;
    name: string;
    asset_type: string | null;
    image_url: string | null;
  } | null;
};

type FieldQuoteRow = {
  id: string;
  project_id: string | null;
  business_id: string;
  customer_id: string | null;
  resource_id: string | null;
  status: string;
  is_measurement: boolean;
  start_date: string | null;
  start_time: string | null;
  end_date: string | null;
  end_time: string | null;
  field_quote_address: string | null;
  notes: string | null;
  technician_notes: string | null;
  created_at: string | null;
  updated_at: string | null;
  customer: {
    first_name: string;
    last_name: string;
    email: string | null;
    phone: string | null;
  } | null;
  resource: {
    id: string;
    first_name: string;
    last_name: string;
    avatar_url?: string | null;
  } | null;
  project: { id: string; title: string | null } | null;
  pipeline_opportunities_id: number | null;
  opportunity: { id: number; title: string | null; value: number | null; status: string | null } | null;
  project_field_quote_resources: FieldQuoteResourceLink[];
  project_field_quote_assets: FieldQuoteAssetLink[];
};

interface FieldQuoteDetailModalProps {
  isOpen: boolean;
  fieldQuoteId: string | null;
  onClose: () => void;
  onUpdated?: () => void;
}

const cardClass =
  'bg-white rounded-[12px] border border-gray-200/60 p-4 shadow-[0_1px_2px_rgba(0,0,0,0.05)]';

/** Shape expected by `BookingResourceList` (matches booking `booking_resources` rows). */
function buildFieldQuoteResourcesForList(row: FieldQuoteRow): any[] {
  const links = row.project_field_quote_resources || [];
  const fromJunction = links
    .filter((l): l is FieldQuoteResourceLink => Boolean(l.resource?.id))
    .map(l => ({
      id: l.id,
      resource_id: l.resource_id,
      role: l.role || 'technician',
      resource: l.resource
    }));
  const junctionIds = new Set(fromJunction.map(j => j.resource_id));
  if (row.resource_id && row.resource && !junctionIds.has(row.resource_id)) {
    return [
      {
        id: `legacy-primary-${row.resource_id}`,
        resource_id: row.resource_id,
        role: 'technician',
        resource: row.resource
      },
      ...fromJunction
    ];
  }
  return fromJunction;
}

/** Same shape as booking `booking_assets` rows for `BookingResourceList`. */
function buildFieldQuoteAssetsForList(row: FieldQuoteRow): any[] {
  return (row.project_field_quote_assets || [])
    .filter((l): l is FieldQuoteAssetLink => Boolean(l.asset?.id))
    .map(l => ({
      id: l.id,
      asset_id: l.asset_id,
      asset: l.asset
    }));
}

function fieldQuoteAssetScheduleBounds(row: FieldQuoteRow): { start_date: string | null; end_date: string | null } {
  const start_date =
    row.start_date && row.start_time ? `${row.start_date} ${row.start_time}` : null;
  const end_date =
    row.end_date && row.end_time ? `${row.end_date} ${row.end_time}` : start_date;
  return { start_date, end_date };
}

function fieldQuoteCustomerForBookingInfo(row: FieldQuoteRow) {
  return {
    id: row.customer_id || '',
    first_name: row.customer?.first_name || '',
    last_name: row.customer?.last_name || '',
    email: row.customer?.email ?? null,
    phone: row.customer?.phone || '',
    business_id: row.business_id,
    type: 'person' as const,
    address: null,
    source: null,
    tags: [] as string[],
    last_interaction: null,
    lifetime_value: 0,
    next_service_date: null,
    referral_source: null,
    preferences: {},
    status: 'active',
    amount_spent: 0,
    credit_balance: 0,
    notes: null,
    tax_exempt: false,
    workflow_exempt: false,
    carfax_exempt: false,
    receive_reminders: true,
    receive_offers: true,
    lat: null,
    lng: null,
    is_supplier: false,
    repeat: false,
    created_at: row.created_at || '',
    updated_at: row.updated_at || row.created_at || ''
  };
}

export default function FieldQuoteDetailModal({
  isOpen,
  fieldQuoteId,
  onClose,
  onUpdated
}: FieldQuoteDetailModalProps) {
  const { selectedBusiness } = useBusiness();
  const { canAccess } = usePermissionCheck();
  const navigate = useNavigate();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [loading, setLoading] = useState(false);
  const [row, setRow] = useState<FieldQuoteRow | null>(null);
  const [statusSaving, setStatusSaving] = useState(false);
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [selectResourceOpen, setSelectResourceOpen] = useState(false);
  const [assetsEnabled, setAssetsEnabled] = useState(false);
  const [customerDetailsOpen, setCustomerDetailsOpen] = useState(false);
  const [customerForDetails, setCustomerForDetails] = useState<Customer | null>(null);
  const [isMobile, setIsMobile] = useState(typeof window !== 'undefined' && window.innerWidth < 768);
  const [fieldQuoteMenuOpen, setFieldQuoteMenuOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [convertOpen, setConvertOpen] = useState(false);
  const [convertCustomer, setConvertCustomer] = useState<Customer | null>(null);
  const [techNotesDraft, setTechNotesDraft] = useState('');
  const [techNotesSaving, setTechNotesSaving] = useState(false);
  const [photosOpen, setPhotosOpen] = useState(false);
  const [photos, setPhotos] = useState<FieldQuotePhoto[]>([]);

  const loadPhotos = useCallback(async () => {
    if (!fieldQuoteId) return;
    setPhotos(await listFieldQuotePhotos(fieldQuoteId));
  }, [fieldQuoteId]);

  useEffect(() => {
    if (isOpen && fieldQuoteId) loadPhotos();
    else setPhotos([]);
  }, [isOpen, fieldQuoteId, loadPhotos]);

  const handleAddPhotos = useCallback(async (files: File[]) => {
    if (!selectedBusiness?.id || !fieldQuoteId) return;
    for (const file of files) {
      try {
        await uploadFieldQuotePhoto({ businessId: selectedBusiness.id, fieldQuoteId, file });
      } catch (e) {
        console.error('Field quote photo upload failed:', e);
        toast.error(e instanceof Error ? e.message : 'Photo upload failed');
      }
    }
    await loadPhotos();
  }, [selectedBusiness?.id, fieldQuoteId, loadPhotos]);

  const handleDeletePhoto = useCallback(async (photoId: string) => {
    const ok = await deleteFieldQuotePhoto(photoId);
    if (ok) setPhotos((prev) => prev.filter((p) => p.id !== photoId));
    else toast.error('Failed to delete photo');
  }, []);

  // Keep the technician-notes draft in sync with the loaded field quote.
  useEffect(() => {
    setTechNotesDraft(row?.technician_notes || '');
  }, [row?.id, row?.technician_notes]);

  const handleSaveTechNotes = async () => {
    if (!row || !supabase) return;
    setTechNotesSaving(true);
    try {
      const { error } = await supabase
        .from('project_field_quotes')
        .update({ technician_notes: techNotesDraft.trim() || null })
        .eq('id', row.id);
      if (error) throw error;
      toast.success('Technician notes saved');
      await load();
      onUpdated?.();
    } catch (e) {
      console.error('Error saving technician notes:', e);
      toast.error('Failed to save notes');
    } finally {
      setTechNotesSaving(false);
    }
  };
  const [deleteLoading, setDeleteLoading] = useState(false);
  const fieldQuoteMenuRef = useRef<HTMLDivElement>(null);

  const [activeTab, setActiveTab] = useState<'overview' | 'measurements'>('overview');
  const [measurements, setMeasurements] = useState<ProjectMeasurement[]>([]);
  const [isLoadingMeasurements, setIsLoadingMeasurements] = useState(false);
  const [editingMeasurement, setEditingMeasurement] = useState<ProjectMeasurement | null>(null);
  const [isAddMeasurementModalOpen, setIsAddMeasurementModalOpen] = useState(false);
  const [measurementToDelete, setMeasurementToDelete] = useState<ProjectMeasurement | null>(null);
  const [isDeleteMeasurementModalOpen, setIsDeleteMeasurementModalOpen] = useState(false);
  const canEditMeasurements = canAccess.editBookingDetails;

  const loadMeasurements = useCallback(async () => {
    if (!fieldQuoteId) return;
    setIsLoadingMeasurements(true);
    try {
      const data = await getFieldQuoteMeasurements(fieldQuoteId);
      setMeasurements(data);
    } catch (e) {
      console.error('FieldQuoteDetailModal loadMeasurements:', e);
    } finally {
      setIsLoadingMeasurements(false);
    }
  }, [fieldQuoteId]);

  const handleSaveMeasurement = useCallback(
    async (params: AddProjectMeasurementParams | UpdateProjectMeasurementParams) => {
      if (editingMeasurement) {
        await updateProjectMeasurement(editingMeasurement.id, params as UpdateProjectMeasurementParams);
      } else {
        await addProjectMeasurement(params as AddProjectMeasurementParams);
      }
      await loadMeasurements();
    },
    [editingMeasurement, loadMeasurements]
  );

  const handleConfirmDeleteMeasurement = useCallback(async () => {
    if (!measurementToDelete) return;
    try {
      await deleteProjectMeasurement(measurementToDelete.id);
      setIsDeleteMeasurementModalOpen(false);
      setMeasurementToDelete(null);
      await loadMeasurements();
      toast.success('Measurement deleted');
    } catch (e) {
      console.error(e);
      toast.error('Failed to delete measurement');
    }
  }, [measurementToDelete, loadMeasurements]);

  const load = useCallback(async () => {
    if (!fieldQuoteId || !selectedBusiness?.id || !supabase) return;
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('project_field_quotes')
        .select(
          `
          id,
          project_id,
          business_id,
          customer_id,
          resource_id,
          status,
          is_measurement,
          start_date,
          start_time,
          end_date,
          end_time,
          field_quote_address,
          notes,
          technician_notes,
          created_at,
          updated_at,
          pipeline_opportunities_id,
          customer:customers(first_name, last_name, email, phone),
          resource:resources!project_field_quotes_resource_id_fkey(id, first_name, last_name, avatar_url),
          project:projects(id, title),
          opportunity:pipeline_opportunities(id, title, value, status),
          project_field_quote_resources(
            id,
            resource_id,
            role,
            resource:resources(id, first_name, last_name, avatar_url)
          ),
          project_field_quote_assets(
            id,
            asset_id,
            start_date,
            end_date,
            asset:assets(id, name, asset_type, image_url)
          )
        `
        )
        .eq('id', fieldQuoteId)
        .eq('business_id', selectedBusiness.id)
        .maybeSingle();

      if (error) throw error;
      const rawOpp = (data as any)?.opportunity;
      const opportunity =
        rawOpp == null
          ? null
          : Array.isArray(rawOpp)
            ? rawOpp[0] ?? null
            : rawOpp;
      let normalized = data
        ? {
            ...data,
            opportunity: opportunity as FieldQuoteRow['opportunity'],
            project_field_quote_resources: data.project_field_quote_resources ?? [],
            project_field_quote_assets: data.project_field_quote_assets ?? []
          }
        : null;

      const oppId = (data as any)?.pipeline_opportunities_id as number | null | undefined;
      if (normalized && oppId != null && !normalized.opportunity) {
        const { data: oppRow, error: oppErr } = await supabase
          .from('pipeline_opportunities')
          .select('id, title, value, status')
          .eq('id', oppId)
          .maybeSingle();
        if (!oppErr && oppRow) {
          normalized = {
            ...normalized,
            opportunity: oppRow as FieldQuoteRow['opportunity']
          };
        }
      }

      setRow(normalized as FieldQuoteRow | null);
    } catch (e) {
      console.error('FieldQuoteDetailModal load:', e);
      toast.error('Could not load field quote');
      setRow(null);
    } finally {
      setLoading(false);
    }
  }, [fieldQuoteId, selectedBusiness?.id]);

  useEffect(() => {
    if (isOpen) {
      const timer = setTimeout(() => setIsModalVisible(true), 50);
      return () => clearTimeout(timer);
    }
    setIsModalVisible(false);
  }, [isOpen]);

  useEffect(() => {
    if (isOpen && fieldQuoteId) {
      document.body.classList.add('modal-open-ios');
      document.body.classList.add('modal-open');
      load();
      void loadMeasurements();
    } else {
      setRow(null);
      setScheduleOpen(false);
      setSelectResourceOpen(false);
      setCustomerDetailsOpen(false);
      setCustomerForDetails(null);
      setActiveTab('overview');
      setMeasurements([]);
      setEditingMeasurement(null);
      setIsAddMeasurementModalOpen(false);
      setMeasurementToDelete(null);
      setIsDeleteMeasurementModalOpen(false);
    }
    return () => {
      document.body.classList.remove('modal-open-ios');
      document.body.classList.remove('modal-open');
    };
  }, [isOpen, fieldQuoteId, load, loadMeasurements]);

  // Realtime: keep the measurements list / tab count in sync.
  useEffect(() => {
    if (!isOpen || !fieldQuoteId || !supabase) return;
    const channel = supabase
      .channel(`field-quote-measurements-${fieldQuoteId}`)
      .on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: 'project_measurements',
          filter: `field_quote_id=eq.${fieldQuoteId}`
        },
        () => void loadMeasurements()
      )
      .subscribe();
    return () => {
      void supabase.removeChannel(channel);
    };
  }, [isOpen, fieldQuoteId, loadMeasurements]);

  useEffect(() => {
    if (!isOpen || !selectedBusiness?.id || !supabase) {
      setAssetsEnabled(false);
      return;
    }
    let cancelled = false;
    supabase
      .rpc('get_app_setting', {
        p_business_id: selectedBusiness.id,
        p_app_slug: 'teams',
        p_setting_key: 'assets_enabled',
        p_default_value: false
      })
      .then(({ data, error: settingError }) => {
        if (cancelled) return;
        if (!settingError && data !== null) setAssetsEnabled(!!data);
        else setAssetsEnabled(false);
      })
      .catch(() => {
        if (!cancelled) setAssetsEnabled(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, selectedBusiness?.id]);

  useEffect(() => {
    const handleCloseAllModals = () => {
      if (isOpen) onClose();
    };
    window.addEventListener('closeAllModals', handleCloseAllModals);
    return () => window.removeEventListener('closeAllModals', handleCloseAllModals);
  }, [isOpen, onClose]);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (!fieldQuoteMenuOpen) return;
    const onPointerDown = (e: PointerEvent) => {
      const el = fieldQuoteMenuRef.current;
      const target = e.target as Node | null;
      if (el && target && !el.contains(target)) {
        const menuPanel = document.querySelector('[data-field-quote-menu-panel]');
        if (menuPanel && menuPanel.contains(target)) return;
        setFieldQuoteMenuOpen(false);
      }
    };
    document.addEventListener('pointerdown', onPointerDown);
    return () => document.removeEventListener('pointerdown', onPointerDown);
  }, [fieldQuoteMenuOpen]);

  const assignedResourceIdsForModal = useMemo(() => {
    if (!row) return [] as { id: string }[];
    const ids = new Set<string>();
    for (const l of row.project_field_quote_resources || []) {
      if (l.resource_id) ids.add(l.resource_id);
    }
    if (row.resource_id) ids.add(row.resource_id);
    return [...ids].map(id => ({ id }));
  }, [row]);

  const assignedAssetIdsForModal = useMemo(() => {
    if (!row) return [] as string[];
    return (row.project_field_quote_assets || []).map(a => a.asset_id).filter(Boolean);
  }, [row]);

  const syncFieldQuotePrimaryResource = useCallback(async () => {
    if (!row?.id || !selectedBusiness?.id || !supabase) return;
    const { data: links, error } = await supabase
      .from('project_field_quote_resources')
      .select('resource_id')
      .eq('project_field_quote_id', row.id)
      .order('created_at', { ascending: true });
    if (error) throw error;
    const primary = links?.[0]?.resource_id ?? null;
    const { error: upErr } = await supabase
      .from('project_field_quotes')
      .update({ resource_id: primary })
      .eq('id', row.id)
      .eq('business_id', selectedBusiness.id);
    if (upErr) throw upErr;
  }, [row?.id, selectedBusiness?.id]);

  const handleRemoveFieldQuoteResource = useCallback(
    async (resourceId: string) => {
      if (!row?.id || !selectedBusiness?.id || !supabase) return;
      try {
        await supabase
          .from('project_field_quote_resources')
          .delete()
          .eq('project_field_quote_id', row.id)
          .eq('resource_id', resourceId);

        await syncFieldQuotePrimaryResource();
        triggerGcalSync(row.id, 'update', selectedBusiness.id, 'field_quote');
        await load();
        onUpdated?.();
        toast.success('Resource removed');
      } catch (e) {
        console.error(e);
        toast.error('Failed to remove resource');
      }
    },
    [row?.id, selectedBusiness?.id, load, onUpdated, syncFieldQuotePrimaryResource]
  );

  const handleSelectResourceForFieldQuote = useCallback(
    async (resource: { id: string }) => {
      if (!resource?.id || resource.id === 'unassigned' || !row?.id || !selectedBusiness?.id || !supabase) return;
      const existing = new Set<string>();
      for (const l of row.project_field_quote_resources || []) {
        if (l.resource_id) existing.add(l.resource_id);
      }
      if (row.resource_id) existing.add(row.resource_id);
      if (existing.has(resource.id)) {
        toast.error('Already assigned');
        return;
      }
      try {
        const { error } = await supabase.from('project_field_quote_resources').insert({
          project_field_quote_id: row.id,
          resource_id: resource.id,
          role: 'technician'
        });
        if (error) throw error;

        await syncFieldQuotePrimaryResource();
        triggerGcalSync(row.id, 'update', selectedBusiness.id, 'field_quote');

        // Reverse proximity check (same as assigning a resource to a booking):
        // if this resource is already near the field quote address, auto-start
        // their job timer. Best-effort — respects the auto GPS settings.
        supabase.functions.invoke('check-booking-proximity', {
          body: { field_quote_id: row.id, business_id: selectedBusiness.id }
        }).catch(err => {
          console.error('Error calling check-booking-proximity for field quote:', err);
        });

        await load();
        onUpdated?.();
        toast.success('Resource added');
      } catch (e) {
        console.error(e);
        toast.error('Failed to add resource');
      }
    },
    [row, selectedBusiness?.id, load, onUpdated, syncFieldQuotePrimaryResource]
  );

  const handleRemoveFieldQuoteAsset = useCallback(
    async (assetId: string) => {
      if (!row?.id || !selectedBusiness?.id || !supabase) return;
      try {
        const { error } = await supabase
          .from('project_field_quote_assets')
          .delete()
          .eq('project_field_quote_id', row.id)
          .eq('asset_id', assetId);
        if (error) throw error;
        triggerGcalSync(row.id, 'update', selectedBusiness.id, 'field_quote');
        await load();
        onUpdated?.();
        toast.success('Asset removed');
      } catch (e) {
        console.error(e);
        toast.error('Failed to remove asset');
      }
    },
    [row?.id, selectedBusiness?.id, load, onUpdated]
  );

  const handleSelectAssetForFieldQuote = useCallback(
    async (asset: { id: string }) => {
      if (!asset?.id || !row?.id || !selectedBusiness?.id || !supabase) return;
      const existing = new Set((row.project_field_quote_assets || []).map(a => a.asset_id));
      if (existing.has(asset.id)) {
        toast.error('Already assigned');
        return;
      }
      const { start_date, end_date } = fieldQuoteAssetScheduleBounds(row);
      try {
        const { error } = await supabase.from('project_field_quote_assets').insert({
          project_field_quote_id: row.id,
          asset_id: asset.id,
          start_date,
          end_date
        });
        if (error) throw error;
        triggerGcalSync(row.id, 'update', selectedBusiness.id, 'field_quote');
        await load();
        onUpdated?.();
        toast.success('Asset added');
      } catch (e) {
        console.error(e);
        toast.error('Failed to add asset');
      }
    },
    [row, selectedBusiness?.id, load, onUpdated]
  );

  const handleFieldQuoteAddressUpdate = useCallback(
    async (address: string, _lat?: number, _lng?: number) => {
      if (!row?.id || !selectedBusiness?.id || !supabase) return;
      const prevAddress = row.field_quote_address;
      try {
        setRow(prev => (prev ? { ...prev, field_quote_address: address || null } : prev));
        const { error } = await supabase
          .from('project_field_quotes')
          .update({ field_quote_address: address || null })
          .eq('id', row.id)
          .eq('business_id', selectedBusiness.id);

        if (error) throw error;

        triggerGcalSync(row.id, 'update', selectedBusiness.id, 'field_quote');
        onUpdated?.();
        toast.success('Address updated');
      } catch (e) {
        console.error(e);
        toast.error('Failed to update address');
        setRow(prev => (prev ? { ...prev, field_quote_address: prevAddress } : prev));
      }
    },
    [row, selectedBusiness?.id, onUpdated]
  );

  const handleScheduleInlineUpdate = async (data: {
    startDate: string;
    startTime: string;
    endDate: string;
    endTime: string;
  }) => {
    if (!row?.id || !selectedBusiness?.id || !supabase) return;
    try {
      const { error } = await supabase
        .from('project_field_quotes')
        .update({
          start_date: data.startDate || null,
          start_time: data.startTime || null,
          end_date: data.endDate || null,
          end_time: data.endTime || null
        })
        .eq('id', row.id)
        .eq('business_id', selectedBusiness.id);

      if (error) throw error;

      const fqStart =
        data.startDate && data.startTime ? `${data.startDate} ${data.startTime}` : null;
      const fqEnd =
        data.endDate && data.endTime ? `${data.endDate} ${data.endTime}` : fqStart;
      await supabase
        .from('project_field_quote_assets')
        .update({ start_date: fqStart, end_date: fqEnd })
        .eq('project_field_quote_id', row.id);

      triggerGcalSync(row.id, 'update', selectedBusiness.id, 'field_quote');
      await load();
      onUpdated?.();
      toast.success('Schedule updated');
    } catch (e) {
      console.error(e);
      toast.error('Failed to update schedule');
      throw e;
    }
  };

  const handleStatusChange = async (next: string) => {
    if (!row?.id || !selectedBusiness?.id || !supabase) return;
    setStatusSaving(true);
    try {
      const { error } = await supabase
        .from('project_field_quotes')
        .update({ status: next })
        .eq('id', row.id)
        .eq('business_id', selectedBusiness.id);

      if (error) throw error;

      triggerGcalSync(row.id, 'update', selectedBusiness.id, 'field_quote');
      setRow(prev => (prev ? { ...prev, status: next } : prev));
      toast.success('Status updated');
      onUpdated?.();
    } catch (e) {
      console.error(e);
      toast.error('Failed to update status');
    } finally {
      setStatusSaving(false);
    }
  };

  const openProject = () => {
    if (!row?.project_id) return;
    onClose();
    navigate('/projects', { state: { openProjectId: row.project_id } });
  };

  const openOpportunity = () => {
    const oppId = row?.opportunity?.id ?? row?.pipeline_opportunities_id;
    if (oppId == null) return;
    onClose();
    navigate('/crm', { state: { openOpportunityId: oppId } });
  };

  const handleConfirmDeleteFieldQuote = async () => {
    if (!row?.id || !selectedBusiness?.id || !supabase) return;
    setDeleteLoading(true);
    try {
      triggerGcalSync(row.id, 'delete', selectedBusiness.id, 'field_quote');
      const { error } = await supabase
        .from('project_field_quotes')
        .delete()
        .eq('id', row.id)
        .eq('business_id', selectedBusiness.id);

      if (error) throw error;

      toast.success(row.is_measurement ? 'Field measurement deleted' : 'Field quote deleted');
      setDeleteConfirmOpen(false);
      setFieldQuoteMenuOpen(false);
      onUpdated?.();
      onClose();
    } catch (e) {
      console.error(e);
      toast.error(row.is_measurement ? 'Failed to delete field measurement' : 'Failed to delete field quote');
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleOpenCustomerDetails = useCallback(() => {
    const customerId = row?.customer_id;
    if (!customerId) return;
    void (async () => {
      try {
        const customer = await getCustomer(customerId);
        setCustomerForDetails(customer);
        setCustomerDetailsOpen(true);
      } catch (e) {
        console.error(e);
        toast.error('Could not load customer');
      }
    })();
  }, [row?.customer_id]);

  if (!isOpen && !isModalVisible) return null;

  const headerTitle = row?.is_measurement ? 'Field measurements' : 'Field quote';

  const modalContent = (
    <div
      className={`fixed inset-0 z-[9999] booking-info-modal ios-modal-fix ${isModalVisible ? '' : 'pointer-events-none'}`}
      aria-modal="true"
      role="dialog"
      style={{
        zIndex: 99999,
        isolation: 'isolate',
        WebkitOverflowScrolling: 'touch',
        WebkitBackfaceVisibility: 'hidden',
        position: 'fixed',
        top: 'env(safe-area-inset-top, 0px)',
        left: 'env(safe-area-inset-left, 0px)',
        right: 'env(safe-area-inset-right, 0px)',
        bottom: 'env(safe-area-inset-bottom, 0px)',
        height: 'calc(100% - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px))',
        width: 'calc(100% - env(safe-area-inset-left, 0px) - env(safe-area-inset-right, 0px))'
      }}
    >
      <div
        className={`fixed inset-0 z-[99900] ios-modal-backdrop ${isModalVisible ? 'opacity-100' : 'opacity-0'}`}
        style={{
          position: 'fixed',
          zIndex: 99900,
          top: 'env(safe-area-inset-top, 0px)',
          left: 'env(safe-area-inset-left, 0px)',
          right: 'env(safe-area-inset-right, 0px)',
          bottom: 'env(safe-area-inset-bottom, 0px)',
          pointerEvents: isModalVisible ? 'auto' : 'none'
        }}
      >
        <div
          className={`fixed inset-0 ios-modal-content transition-colors duration-200 ${
            isModalVisible ? 'bg-gray-50' : 'bg-black'
          }`}
          style={{
            WebkitBackfaceVisibility: 'hidden',
            zIndex: 99950,
            position: 'fixed',
            top: 'env(safe-area-inset-top, 0px)',
            left: 'env(safe-area-inset-left, 0px)',
            right: 'env(safe-area-inset-right, 0px)',
            bottom: 'env(safe-area-inset-bottom, 0px)',
            width: 'calc(100% - env(safe-area-inset-left, 0px) - env(safe-area-inset-right, 0px))',
            height: 'calc(100% - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px))',
            display: isOpen ? 'block' : 'none'
          }}
        >
          <div
            className={`h-full flex flex-col transition-opacity duration-200 ${
              isModalVisible ? 'opacity-100' : 'opacity-0'
            }`}
            style={{
              paddingTop: 0,
              position: 'relative',
              zIndex: 9999
            }}
          >
            {/* Header — same chrome as BookingHeader */}
            <div
              className={`relative ${isMobile ? 'bg-black' : 'bg-black'} border-b ${
                isMobile ? 'border-gray-800/50' : 'border-gray-200'
              } shadow-sm`}
              style={{
                zIndex: 10,
                width: '100%',
                ...(isMobile && {
                  backdropFilter: 'blur(20px)',
                  WebkitBackdropFilter: 'blur(20px)',
                  backgroundColor: 'rgba(0, 0, 0, 0.95)'
                })
              }}
            >
              <div className={`flex items-start justify-between ${isMobile ? 'px-3 py-2.5' : 'px-4 py-3'}`}>
                <div className="flex items-center flex-shrink-0">
                  <button
                    type="button"
                    onClick={onClose}
                    className={`flex items-center justify-center ${isMobile ? 'w-10 h-10' : 'w-8 h-8'} rounded-full text-white transition-all duration-200 active:scale-95 touch-manipulation`}
                    style={{ WebkitTapHighlightColor: 'transparent', backgroundColor: 'transparent' }}
                  >
                    <X className={isMobile ? 'h-6 w-6' : 'h-5 w-5'} />
                  </button>
                </div>

                <div className="flex-1 flex justify-center px-2 min-w-0">
                  <div className="flex-1 min-w-0 w-full">
                    <div className="text-center">
                      {row?.project?.title && row.project_id && (
                        <button
                          type="button"
                          onClick={openProject}
                          className="inline-flex items-center gap-1.5 px-2.5 py-1 mb-1.5 bg-blue-500/20 hover:bg-blue-500/30 active:bg-blue-500/40 rounded-full transition-colors touch-manipulation"
                          style={{ WebkitTapHighlightColor: 'transparent' }}
                        >
                          <Folder className="h-3 w-3 text-blue-400" />
                          <span className="text-[11px] font-medium text-blue-300">{row.project.title}</span>
                        </button>
                      )}
                      {(row?.opportunity?.id != null || row?.pipeline_opportunities_id != null) && (
                        <button
                          type="button"
                          onClick={openOpportunity}
                          className="inline-flex items-center gap-1.5 px-2.5 py-1 mb-1.5 bg-blue-500/20 hover:bg-blue-500/30 active:bg-blue-500/40 rounded-full transition-colors touch-manipulation"
                          style={{ WebkitTapHighlightColor: 'transparent' }}
                        >
                          <TrendingUp className="h-3 w-3 text-blue-400 flex-shrink-0" />
                          <span className="text-[11px] font-medium text-blue-300 whitespace-nowrap">
                            {row.opportunity?.title?.trim() || 'Opportunity'}
                          </span>
                        </button>
                      )}
                      <h2
                        className={`${isMobile ? 'text-[16px] font-semibold' : 'text-lg font-semibold'} text-white break-words`}
                        style={{
                          fontFamily:
                            '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", system-ui, sans-serif',
                          letterSpacing: '-0.01em',
                          wordBreak: 'break-word',
                          overflowWrap: 'break-word',
                          hyphens: 'auto'
                        }}
                      >
                        {loading ? 'Field quote' : headerTitle}
                      </h2>
                      {!isMobile && row && (
                        <div className="flex items-center justify-center mt-0.5">
                          <div
                            className={`h-1.5 w-1.5 sm:h-2 sm:w-2 rounded-full mr-1.5 sm:mr-2 shadow-sm ${
                              normalizeFqStatus(row.status) === 'completed'
                                ? 'bg-green-500'
                                : normalizeFqStatus(row.status) === 'in_progress'
                                  ? 'bg-yellow-500'
                                  : 'bg-blue-500'
                            }`}
                          />
                          <span className="text-sm text-gray-300 truncate">
                            {normalizeFqStatus(row.status) === 'completed'
                              ? 'Field quote completed'
                              : normalizeFqStatus(row.status) === 'in_progress'
                                ? 'Field quote in progress'
                                : 'Field quote scheduled'}
                          </span>
                        </div>
                      )}
                      {isMobile && row && (
                        <div className="flex items-center justify-center space-x-1 mt-2 flex-nowrap">
                          {(
                            [
                              { value: 'scheduled', label: 'Scheduled' },
                              { value: 'in_progress', label: 'In Progress' },
                              { value: 'completed', label: 'Completed' }
                            ] as const
                          ).map((s, index) => {
                            const fqStatus = normalizeFqStatus(row.status);
                            const statusIndex = ['scheduled', 'in_progress', 'completed'].findIndex(
                              st => st === fqStatus
                            );
                            const isActive = index <= statusIndex;
                            const isNext = index === statusIndex + 1;

                            return (
                              <React.Fragment key={s.value}>
                                <button
                                  type="button"
                                  onClick={() => {
                                    if (!statusSaving && (isActive || isNext)) {
                                      if (navigator.vibrate) navigator.vibrate(10);
                                      void handleStatusChange(barStatusToDb(s.value));
                                    }
                                  }}
                                  disabled={statusSaving || (!isActive && !isNext)}
                                  className={`text-[12px] font-semibold transition-all duration-200 px-2.5 py-1.5 rounded-[8px] min-w-[70px] whitespace-nowrap touch-manipulation active:scale-[0.96] flex-shrink-0 ${
                                    isActive
                                      ? fqStatus === 'scheduled' && index === 0
                                        ? 'text-blue-600 bg-blue-50 shadow-sm'
                                        : fqStatus === 'in_progress' && index <= 1
                                          ? 'text-amber-600 bg-amber-50 shadow-sm'
                                          : fqStatus === 'completed' && index <= 2
                                            ? 'text-green-600 bg-green-50 shadow-sm'
                                            : 'text-blue-600 bg-blue-50 shadow-sm'
                                      : isNext && !statusSaving
                                        ? 'text-gray-400 bg-white/10 hover:bg-white/15 active:bg-white/20'
                                        : 'text-gray-500 bg-white/5'
                                  }`}
                                  style={{
                                    touchAction: 'manipulation',
                                    WebkitTapHighlightColor: 'transparent',
                                    fontFamily:
                                      '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", system-ui, sans-serif',
                                    letterSpacing: '-0.01em'
                                  }}
                                >
                                  {s.label}
                                </button>
                                {index < 2 && (
                                  <ChevronRight
                                    className={`h-3 w-3 flex-shrink-0 ${
                                      index < statusIndex ? 'text-white/60' : 'text-white/30'
                                    }`}
                                  />
                                )}
                              </React.Fragment>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div
                  className="flex items-center justify-end flex-shrink-0 gap-0.5"
                  ref={fieldQuoteMenuRef}
                >
                  {!loading && row && (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (navigator.vibrate) navigator.vibrate(10);
                        setPhotosOpen(true);
                      }}
                      className={`relative inline-flex items-center justify-center ${isMobile ? 'w-10 h-10' : 'w-8 h-8'} text-white rounded-full transition-all duration-200 active:scale-95 touch-manipulation hover:bg-white/10`}
                      style={{ WebkitTapHighlightColor: 'transparent' }}
                      title="Photos"
                    >
                      <Camera className={isMobile ? 'h-6 w-6' : 'h-5 w-5'} />
                      {photos.length > 0 && (
                        <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-[16px] px-1 flex items-center justify-center text-[10px] font-semibold bg-blue-500 text-white rounded-full">
                          {photos.length}
                        </span>
                      )}
                    </button>
                  )}
                  {!loading && row && canAccess.editBookingDetails && (
                    <>
                      <button
                        type="button"
                        onClick={e => {
                          e.stopPropagation();
                          if (navigator.vibrate) navigator.vibrate(10);
                          setFieldQuoteMenuOpen(v => !v);
                        }}
                        className={`inline-flex items-center justify-center ${isMobile ? 'w-10 h-10' : 'w-8 h-8'} text-white rounded-full transition-all duration-200 active:scale-95 touch-manipulation hover:bg-white/10`}
                        style={{ WebkitTapHighlightColor: 'transparent' }}
                        title="More options"
                        aria-expanded={fieldQuoteMenuOpen}
                        aria-haspopup="menu"
                      >
                        <MoreVertical className={isMobile ? 'h-6 w-6' : 'h-5 w-5'} />
                      </button>

                      {fieldQuoteMenuOpen &&
                        createPortal(
                          <>
                            <div
                              className="fixed inset-0"
                              style={{
                                zIndex: 100100,
                                background: 'rgba(0, 0, 0, 0.4)',
                                backdropFilter: 'blur(8px)',
                                WebkitBackdropFilter: 'blur(8px)'
                              }}
                              onClick={() => setFieldQuoteMenuOpen(false)}
                              aria-hidden
                            />
                            <div
                              data-field-quote-menu-panel
                              className="fixed bg-white rounded-[14px] shadow-lg py-1"
                              style={{
                                zIndex: 100101,
                                left: '50%',
                                top: '50%',
                                transform: 'translate(-50%, -50%)',
                                width: 'calc(100% - 32px)',
                                maxWidth: '320px',
                                boxShadow: '0 10px 40px rgba(0, 0, 0, 0.15), 0 2px 8px rgba(0, 0, 0, 0.1)'
                              }}
                              role="menu"
                              onClick={e => e.stopPropagation()}
                            >
                              {!row.is_measurement && (
                                <button
                                  type="button"
                                  role="menuitem"
                                  className="w-full px-4 py-3.5 text-left text-[17px] text-gray-800 flex items-center transition-colors active:bg-gray-50 touch-manipulation sm:text-base sm:py-3"
                                  style={{ WebkitTapHighlightColor: 'transparent' }}
                                  onClick={async () => {
                                    setFieldQuoteMenuOpen(false);
                                    if (row.customer_id) {
                                      try {
                                        const c = await getCustomer(row.customer_id);
                                        setConvertCustomer(c);
                                      } catch {
                                        setConvertCustomer(null);
                                      }
                                    } else {
                                      setConvertCustomer(null);
                                    }
                                    setTimeout(() => setConvertOpen(true), 50);
                                  }}
                                >
                                  <ExternalLink className="h-5 w-5 mr-3 flex-shrink-0" />
                                  <span className="font-normal">Convert to booking</span>
                                </button>
                              )}
                              <button
                                type="button"
                                role="menuitem"
                                className="w-full px-4 py-3.5 text-left text-[17px] text-red-600 flex items-center transition-colors active:bg-red-50 touch-manipulation sm:text-base sm:py-3"
                                style={{ WebkitTapHighlightColor: 'transparent' }}
                                onClick={() => {
                                  setFieldQuoteMenuOpen(false);
                                  setTimeout(() => setDeleteConfirmOpen(true), 50);
                                }}
                              >
                                <Trash2 className="h-5 w-5 mr-3 flex-shrink-0" />
                                <span className="font-normal">
                                  {row.is_measurement ? 'Delete field measurement' : 'Delete field quote'}
                                </span>
                              </button>
                            </div>
                          </>,
                          document.body
                        )}
                    </>
                  )}
                </div>
              </div>
            </div>

            {!isMobile && row && (
              <BookingStatusBar
                status={normalizeFqStatus(row.status)}
                createdAt={row.created_at || ''}
                completedAt={
                  normalizeFqStatus(row.status) === 'completed'
                    ? row.updated_at || row.created_at || null
                    : null
                }
                onStatusChange={s => void handleStatusChange(barStatusToDb(s))}
                readOnly={statusSaving}
                canEdit={true}
              />
            )}

            {/* Tabs — same style as ProjectInfoModal / BookingInfoModal */}
            {!loading && row && (
              <div className="bg-white border-b border-gray-200">
                <div className="max-w-5xl mx-auto">
                  <nav className="flex overflow-x-auto scrollbar-hide" aria-label="Tabs">
                    {([
                      { id: 'overview', label: 'Overview' },
                      {
                        id: 'measurements',
                        label: measurements.length > 0 ? `Measurements (${measurements.length})` : 'Measurements'
                      }
                    ] as const).map(tab => (
                      <button
                        key={tab.id}
                        type="button"
                        onClick={() => setActiveTab(tab.id)}
                        className={`flex-1 min-w-[80px] py-3 sm:py-4 px-2 sm:px-4 border-b-2 text-xs sm:text-sm font-bold text-center whitespace-nowrap transition-colors ${
                          activeTab === tab.id
                            ? 'border-blue-600 text-blue-700 bg-blue-50'
                            : 'border-transparent text-gray-500 hover:text-blue-700 hover:border-blue-400 hover:bg-blue-50'
                        }`}
                        style={{ touchAction: 'manipulation', WebkitTapHighlightColor: 'transparent' }}
                      >
                        {tab.label}
                      </button>
                    ))}
                  </nav>
                </div>
              </div>
            )}

            {/* Body — same scroll container as BookingInfoModal */}
            <div
              className="flex-1 overflow-y-auto relative min-h-0"
              style={{
                WebkitOverflowScrolling: 'touch',
                overflowX: 'hidden'
              }}
            >
              <div
                className="max-w-5xl mx-auto py-1 px-2 sm:py-6 sm:px-6"
                style={{
                  paddingBottom: 'calc(1rem + env(safe-area-inset-bottom, 0px))'
                }}
              >
                <div className="space-y-3 sm:space-y-4">
                  {loading && (
                    <div className="flex justify-center py-16">
                      <div className="h-8 w-8 border-2 border-gray-200 border-t-blue-600 rounded-full animate-spin" />
                    </div>
                  )}

                  {!loading && row && activeTab === 'overview' && (
                    <>
                      {(row.opportunity?.id != null || row.pipeline_opportunities_id != null) && (
                        <div className="flex flex-col sm:flex-row gap-2">
                          <button
                            type="button"
                            onClick={openOpportunity}
                            className="inline-flex items-center justify-center gap-2 rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm font-semibold text-gray-800 hover:bg-gray-50 transition-colors shadow-[0_1px_2px_rgba(0,0,0,0.05)]"
                          >
                            <TrendingUp className="h-4 w-4" />
                            Open opportunity in CRM
                          </button>
                        </div>
                      )}

                      {/* Schedule — same ScheduleTimeline + shell as mobile booking in BookingInfoModal */}
                      <div className="bg-white rounded-[12px] border border-gray-200/60 shadow-[0_1px_2px_rgba(0,0,0,0.05)]">
                        <div className={isMobile ? 'p-2' : 'p-3'}>
                          <ScheduleTimeline
                            booking={{
                              start_date: row.start_date || '',
                              start_time: row.start_time || '',
                              end_date: row.end_date || '',
                              end_time: row.end_time || '',
                              mobile_booking: true,
                              status: normalizeFqStatus(row.status)
                            }}
                            canEdit={false}
                            readOnly={statusSaving}
                            isEditing={false}
                            isMobile={typeof window !== 'undefined' && window.innerWidth < 640}
                            onRescheduleMobileBooking={() => setScheduleOpen(true)}
                            onUpdateSchedule={statusSaving ? undefined : handleScheduleInlineUpdate}
                          />
                        </div>
                      </div>

                      {/* Customer + address — same shell + BookingCustomerInfo as mobile booking in BookingInfoModal */}
                      <div className="bg-white rounded-[12px] border border-gray-200/60 overflow-hidden shadow-[0_1px_2px_rgba(0,0,0,0.05)]">
                        <div className={isMobile ? 'p-2' : 'p-2'}>
                          <BookingCustomerInfo
                            customer={fieldQuoteCustomerForBookingInfo(row)}
                            mobile_booking
                            canEdit={canAccess.editBookingDetails}
                            onOpenCustomerDetails={
                              row.customer_id &&
                              (canAccess.editBookingDetails || canAccess.customers)
                                ? handleOpenCustomerDetails
                                : undefined
                            }
                            bookingAddress={row.field_quote_address || ''}
                            onEditBookingAddress={
                              canAccess.editBookingDetails
                                ? handleFieldQuoteAddressUpdate
                                : undefined
                            }
                            hideAddressCoordinates
                            businessId={selectedBusiness?.id}
                            onNavigateToAddress={address => {
                              const encoded = encodeURIComponent(address);
                              window.open(
                                `https://www.google.com/maps/dir/?api=1&destination=${encoded}`,
                                '_blank'
                              );
                            }}
                          />
                        </div>
                      </div>

                      {/* Resources — same `BookingResourceList` shell as BookingInfoModal / BookingContent */}
                      <div className="bg-white rounded-[12px] border border-gray-200/60 overflow-hidden shadow-[0_1px_2px_rgba(0,0,0,0.05)]">
                        <BookingResourceList
                          resources={buildFieldQuoteResourcesForList(row)}
                          onRemoveResource={
                            canAccess.editBookingDetails ? handleRemoveFieldQuoteResource : undefined
                          }
                          onAddResource={
                            canAccess.editBookingDetails ? () => setSelectResourceOpen(true) : undefined
                          }
                          assets={assetsEnabled ? buildFieldQuoteAssetsForList(row) : []}
                          onRemoveAsset={
                            canAccess.editBookingDetails && assetsEnabled
                              ? handleRemoveFieldQuoteAsset
                              : undefined
                          }
                          onAddAsset={
                            canAccess.editBookingDetails && assetsEnabled
                              ? () => setSelectResourceOpen(true)
                              : undefined
                          }
                        />
                      </div>

                      {row.notes && (
                        <div className={cardClass}>
                          <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Notes</div>
                          <p className="text-sm text-gray-800 whitespace-pre-wrap">{row.notes}</p>
                        </div>
                      )}

                      {/* Technician notes — editable on-site notes for this field quote */}
                      <div className={cardClass}>
                        <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                          Technician Notes
                        </div>
                        {canAccess.editBookingDetails ? (
                          <>
                            <textarea
                              value={techNotesDraft}
                              onChange={e => setTechNotesDraft(e.target.value)}
                              placeholder="Add notes from the field quote (measurements, access details, customer requests)…"
                              rows={4}
                              className="w-full px-3 py-2.5 text-sm bg-white border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-y"
                              style={{ fontSize: '16px' }}
                            />
                            {techNotesDraft.trim() !== (row.technician_notes || '').trim() && (
                              <div className="flex justify-end gap-2 mt-2">
                                <button
                                  type="button"
                                  onClick={() => setTechNotesDraft(row.technician_notes || '')}
                                  disabled={techNotesSaving}
                                  className="px-3 py-2 text-sm font-semibold text-gray-700 bg-gray-100 hover:bg-gray-200 rounded-xl transition-colors disabled:opacity-50"
                                >
                                  Cancel
                                </button>
                                <button
                                  type="button"
                                  onClick={() => void handleSaveTechNotes()}
                                  disabled={techNotesSaving}
                                  className="px-4 py-2 text-sm font-semibold text-white bg-blue-600 hover:bg-blue-700 active:bg-blue-800 rounded-xl transition-colors disabled:opacity-50"
                                >
                                  {techNotesSaving ? 'Saving…' : 'Save notes'}
                                </button>
                              </div>
                            )}
                          </>
                        ) : row.technician_notes ? (
                          <p className="text-sm text-gray-800 whitespace-pre-wrap">{row.technician_notes}</p>
                        ) : (
                          <p className="text-sm text-gray-400">No technician notes yet.</p>
                        )}
                      </div>

                      {row.project_id && (
                        <div className="flex flex-col sm:flex-row gap-2 pt-1">
                          <button
                            type="button"
                            onClick={openProject}
                            className="inline-flex items-center justify-center gap-2 rounded-xl border border-gray-200 bg-white px-4 py-3 text-sm font-semibold text-gray-800 hover:bg-gray-50 transition-colors"
                          >
                            <ExternalLink className="h-4 w-4" />
                            Open project
                          </button>
                        </div>
                      )}
                    </>
                  )}

                  {!loading && row && activeTab === 'measurements' && (
                    <div
                      className="space-y-2"
                      style={{
                        fontFamily:
                          '-apple-system, BlinkMacSystemFont, "SF Pro Display", "SF Pro Text", system-ui, sans-serif'
                      }}
                    >
                      <div className="bg-white rounded-[12px] border border-gray-200/60 overflow-hidden shadow-[0_1px_2px_rgba(0,0,0,0.05)]">
                        <div className="px-3 py-2 border-b border-gray-200/60">
                          <div className="text-[13px] font-semibold text-gray-900">Measurements</div>
                        </div>

                        {isLoadingMeasurements ? (
                          <div className="px-3 py-4 text-center text-sm text-gray-500">
                            Loading measurements...
                          </div>
                        ) : measurements.length === 0 ? (
                          <>
                            <div className="px-3 py-4 text-center text-sm text-gray-500">
                              No measurements recorded yet
                            </div>
                            {canEditMeasurements && (
                              <div className="px-3 py-2 border-t border-gray-200/60">
                                <button
                                  type="button"
                                  onClick={() => {
                                    setEditingMeasurement(null);
                                    setIsAddMeasurementModalOpen(true);
                                  }}
                                  className="w-full flex items-center justify-center px-3 py-2 bg-gray-50 hover:bg-gray-100 active:bg-gray-200 rounded-[10px] text-[13px] font-medium text-gray-700 transition-all duration-150 active:scale-[0.98] touch-manipulation"
                                  style={{ WebkitTapHighlightColor: 'transparent' }}
                                >
                                  <Plus className="h-4 w-4 mr-1.5" />
                                  Add Measurement
                                </button>
                              </div>
                            )}
                          </>
                        ) : (
                          <div className="divide-y divide-gray-200/60">
                            {measurements.map(measurement => (
                              <MeasurementItem
                                key={measurement.id}
                                measurement={measurement}
                                onEdit={() => {
                                  setEditingMeasurement(measurement);
                                  setIsAddMeasurementModalOpen(true);
                                }}
                                onDelete={() => {
                                  setMeasurementToDelete(measurement);
                                  setIsDeleteMeasurementModalOpen(true);
                                }}
                                canEdit={canEditMeasurements}
                                businessId={selectedBusiness?.id || ''}
                              />
                            ))}
                          </div>
                        )}

                        {canEditMeasurements && measurements.length > 0 && (
                          <div className="px-3 py-2 border-t border-gray-200/60">
                            <button
                              type="button"
                              onClick={() => {
                                setEditingMeasurement(null);
                                setIsAddMeasurementModalOpen(true);
                              }}
                              className="w-full flex items-center justify-center px-3 py-2 bg-gray-50 hover:bg-gray-100 active:bg-gray-200 rounded-[10px] text-[13px] font-medium text-gray-700 transition-all duration-150 active:scale-[0.98] touch-manipulation"
                              style={{ WebkitTapHighlightColor: 'transparent' }}
                            >
                              <Plus className="h-4 w-4 mr-1.5" />
                              Add Measurement
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );

  const scheduleModal =
    row &&
    !loading && (
      <ScheduleFieldQuoteModal
        isOpen={scheduleOpen}
        onClose={() => setScheduleOpen(false)}
        projectId={row.project_id}
        projectTitle={row.project?.title ?? null}
        mode="edit"
        isMeasurement={!!row.is_measurement}
        fieldQuoteToEdit={{
          id: row.id,
          start_date: row.start_date,
          start_time: row.start_time,
          end_date: row.end_date,
          end_time: row.end_time,
          field_quote_address: row.field_quote_address,
          resource: row.resource
            ? {
                id: row.resource.id,
                first_name: row.resource.first_name,
                last_name: row.resource.last_name,
                avatar_url: row.resource.avatar_url ?? null
              }
            : null
        }}
        onSuccess={() => {
          setScheduleOpen(false);
          load();
          onUpdated?.();
        }}
      />
    );

  return (
    <>
      {createPortal(modalContent, document.body)}
      <PhotoCaptureModal
        isOpen={photosOpen}
        onClose={() => setPhotosOpen(false)}
        title={row?.is_measurement ? 'Field measurement photos' : 'Field quote photos'}
        subtitle={row?.field_quote_address || undefined}
        photos={photos.map((p) => ({ id: p.id, url: fieldQuotePhotoUrl(p.file_path), name: p.file_name || undefined }))}
        onAddFiles={handleAddPhotos}
        onDeletePhoto={handleDeletePhoto}
      />
      {scheduleModal}
      <CustomerDetailsModal
        isOpen={customerDetailsOpen}
        onClose={() => {
          setCustomerDetailsOpen(false);
          setCustomerForDetails(null);
          void load();
          onUpdated?.();
        }}
        customer={customerForDetails}
        onUpdate={() => {
          void load();
          onUpdated?.();
        }}
      />
      {row && !loading && (
        <SelectResourceModal
          isOpen={selectResourceOpen}
          onClose={() => setSelectResourceOpen(false)}
          onConfirm={async ({ resources, assets }) => {
            if (!row?.id || !selectedBusiness?.id || !supabase) return;

            const currentResourceIds = new Set(assignedResourceIdsForModal.map(r => r.id));
            const nextResourceIds = new Set(resources.map(r => r.id));
            const currentAssetIds = new Set(assignedAssetIdsForModal);
            const nextAssetIds = new Set(assets.map(a => a.id));

            for (const id of currentResourceIds) {
              if (!nextResourceIds.has(id)) {
                await handleRemoveFieldQuoteResource(id);
              }
            }
            for (const resource of resources) {
              if (!currentResourceIds.has(resource.id)) {
                await handleSelectResourceForFieldQuote(resource);
              }
            }

            if (assetsEnabled) {
              for (const id of currentAssetIds) {
                if (!nextAssetIds.has(id)) {
                  await handleRemoveFieldQuoteAsset(id);
                }
              }
              for (const asset of assets) {
                if (!currentAssetIds.has(asset.id)) {
                  await handleSelectAssetForFieldQuote(asset);
                }
              }
            }
          }}
          selectedResources={assignedResourceIdsForModal}
          selectedAssetIds={assignedAssetIdsForModal}
          assetsEnabled={assetsEnabled}
          confirmLabel="Confirm"
        />
      )}

      {row && convertOpen && (
        <CreateBookingModal
          isOpen={convertOpen}
          onClose={() => setConvertOpen(false)}
          initialCustomer={convertCustomer}
          initialFieldQuoteId={row.id}
          initialOpportunityId={row.pipeline_opportunities_id ?? null}
          onSuccess={() => {
            setConvertOpen(false);
            void load();
            onUpdated?.();
          }}
        />
      )}

      <DeleteConfirmationModal
        isOpen={deleteConfirmOpen}
        onClose={() => !deleteLoading && setDeleteConfirmOpen(false)}
        onConfirm={() => void handleConfirmDeleteFieldQuote()}
        title={row?.is_measurement ? 'Delete field measurement?' : 'Delete field quote?'}
        message={
          row?.is_measurement
            ? 'This will permanently remove this field measurement and its schedule from the calendar. This cannot be undone.'
            : 'This will permanently remove this field quote and its schedule from the calendar. Linked proposals will be unlinked. This cannot be undone.'
        }
        loading={deleteLoading}
        confirmText="Delete"
      />

      {fieldQuoteId && (
        <AddMeasurementModal
          isOpen={isAddMeasurementModalOpen}
          onClose={() => {
            setIsAddMeasurementModalOpen(false);
            setEditingMeasurement(null);
          }}
          onSave={handleSaveMeasurement}
          measurement={editingMeasurement}
          fieldQuoteId={fieldQuoteId}
          businessId={selectedBusiness?.id || ''}
        />
      )}

      <DeleteConfirmationModal
        isOpen={isDeleteMeasurementModalOpen}
        onClose={() => {
          setIsDeleteMeasurementModalOpen(false);
          setMeasurementToDelete(null);
        }}
        onConfirm={() => void handleConfirmDeleteMeasurement()}
        title="Delete measurement?"
        message={
          measurementToDelete
            ? `This will permanently remove "${measurementToDelete.name}" and its photos. This cannot be undone.`
            : 'This will permanently remove this measurement. This cannot be undone.'
        }
        confirmText="Delete"
      />
    </>
  );
}
